/**
 * React-query friendly axios client (replaces the legacy `Executer.js` + `API.js` for
 * migrated services). Throws on failure, forwards `AbortSignal`, unwraps the
 * server envelope (`body.data ?? body`), and picks a per-region client via the
 * injected strategy. Auth is host-injected via `configureApiClient` — this file
 * knows nothing about Redux, `userService`/`authService`, or the platform.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { REGIONS } from '../constants/region.constants';
import { getAssetsServiceBaseURL, getBaseURL } from '../constants/APIconstants';

// Symbol key so a retried (401 → refresh) config can't collide with axios fields.
const RETRIED = Symbol('retried');

type RetriedConfig = InternalAxiosRequestConfig & { [RETRIED]?: boolean };

export interface ApiAuthStrategy {
  /** Bearer token to attach. Omit for cookie hosts (web/back-office). */
  getAccessToken?: () => string | undefined;
  /** Send cookies with requests (cookie hosts). Omit for bearer hosts. */
  withCredentials?: boolean;
  /** Active region key (`REGIONS.US` / `REGIONS.EU`). */
  getRegion: () => string;
  /** Refresh auth after a 401; resolve `true` to retry the request. */
  refresh: () => Promise<boolean>;
}

type UrlVerb = 'get' | 'delete' | 'head' | 'options';
type DataVerb = 'post' | 'put' | 'patch' | 'postForm' | 'putForm' | 'patchForm';

type ConfigMethod = <T = any, R = T, D = any>(config: AxiosRequestConfig<D>) => Promise<R>;

type UrlMethod = <T = any, R = T, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>,
) => Promise<R>;

type DataMethod = <T = any, R = T, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>,
) => Promise<R>;

// Verb methods return unwrapped `Promise<T>` (the interceptor strips the
// envelope), cast once at the exports below.
export type ApiClient = Omit<AxiosInstance, UrlVerb | DataVerb | 'request'> &
  Record<UrlVerb, UrlMethod> &
  Record<DataVerb, DataMethod> &
  UrlMethod &
  ConfigMethod & {
    request: ConfigMethod;
  };

// Single eager instance: the per-region clients exist from module load; the
// strategy is injected once at bootstrap and read lazily per request.
class ApiClientRegistry {
  private strategy: ApiAuthStrategy | null = null;
  private readonly mainClients: Record<string, ApiClient>;
  private readonly assetsClients: Record<string, ApiClient>;

  constructor() {
    this.mainClients = {
      [REGIONS.US]: this.createClient(getBaseURL(REGIONS.US)),
      [REGIONS.EU]: this.createClient(getBaseURL(REGIONS.EU)),
    };
    this.assetsClients = {
      [REGIONS.US]: this.createClient(getAssetsServiceBaseURL(REGIONS.US)),
      [REGIONS.EU]: this.createClient(getAssetsServiceBaseURL(REGIONS.EU)),
    };
  }

  // Set once; re-entry (e.g. HMR re-running the entrypoint) is ignored.
  configure(strategy: ApiAuthStrategy): void {
    if (this.strategy) return;
    this.strategy = strategy;
    if (strategy.withCredentials) {
      Object.values(this.mainClients).forEach(client => (client.defaults.withCredentials = true));
      Object.values(this.assetsClients).forEach(client => (client.defaults.withCredentials = true));
    }
  }

  getMainClient(): ApiClient {
    return this.pickClient(this.mainClients);
  }

  getAssetsClient(): ApiClient {
    return this.pickClient(this.assetsClients);
  }

  private requireStrategy(): ApiAuthStrategy {
    if (!this.strategy) {
      throw new Error(
        'API client not configured — call configureApiClient() at host bootstrap before any request.',
      );
    }
    return this.strategy;
  }

  private pickClient(clients: Record<string, ApiClient>): ApiClient {
    const client = clients[this.requireStrategy().getRegion()] ?? clients[REGIONS.US];
    if (!client) throw new Error('No API client configured for the active region');
    return client;
  }

  private createClient(baseURL: string): ApiClient {
    const client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    // Bearer is set per request (fresh after a refresh); withCredentials is set
    // on the client defaults in configure().
    client.interceptors.request.use(config => {
      const token = this.requireStrategy().getAccessToken?.();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    client.interceptors.response.use(
      response => {
        const body = response.data;
        const data = body?.data !== undefined ? body.data : body;
        // Cast: the interceptor returns `T`, not AxiosResponse (envelope stripped).
        return data as unknown as AxiosResponse;
      },
      async error => {
        const config = error.config as RetriedConfig | undefined;
        if (!config || error.response?.status !== 401 || config[RETRIED]) {
          throw error;
        }
        config[RETRIED] = true;
        const strategy = this.requireStrategy();
        const refreshed = await strategy.refresh();
        if (!refreshed) throw error;
        // axios skips request interceptors on a retried config — re-set the bearer.
        const token = strategy.getAccessToken?.();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return client.request(config);
      },
    );

    return client;
  }
}

const registry = new ApiClientRegistry();

export const configureApiClient = (strategy: ApiAuthStrategy): void => registry.configure(strategy);

// Call on each request (don't cache) so mid-session region switches take effect.
export const getApiClient = (): ApiClient => registry.getMainClient();

export const getAssetsClient = (): ApiClient => registry.getAssetsClient();

export interface RequestOpts {
  signal?: AbortSignal;
}
